import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  deleteSpecialDay,
  fetchSpecialDays,
  upsertSpecialDay,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import { ReservationConflict, SpecialDay, toDateString } from '../../../types/reservation';
import ConfirmDialog from '../../../Components/Dialog/ConfirmDialog';
import {
  formatTimeRange,
  isValidTimeRange,
  SavedSnackbar,
  TabDescription,
  TimeRange,
  TimeRangeListEditor,
  useSaveFeedback,
} from './SettingsShared';

interface Props {
  storeId: string;
}

/** 'YYYY-MM-DD' → 'YYYY/M/D(曜)' 表示 */
const formatDate = (dateStr: string): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const youbi = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}(${youbi})`;
};

/**
 * タブ④特定日。臨時休業・特別営業（日付単位の営業時間上書き）を管理する。
 * upsert/delete の thunk は自動再取得しないため、成功後にこのタブで再フェッチする。
 */
const SpecialDaysTab: React.FC<Props> = ({ storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const specialDays = useSelector((state: RootState) => state.reservationSettings.specialDays);

  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  // ダイアログ状態（editing は編集元。null なら新規追加）
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SpecialDay | null>(null);
  const [date, setDate] = useState('');
  const [isClosed, setIsClosed] = useState(true);
  const [hours, setHours] = useState<TimeRange[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpecialDay | null>(null);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  // 今日から 1 年先までを取得対象とする
  const load = useCallback(() => {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 365);
    return dispatch(
      fetchSpecialDays({ storeId, from: toDateString(from), to: toDateString(to) }),
    ).unwrap();
  }, [dispatch, storeId]);

  useEffect(() => {
    load()
      .catch(showError)
      .finally(() => setLoaded(true));
  }, [load, showError]);

  const openCreate = () => {
    setEditing(null);
    setDate('');
    setIsClosed(true);
    setHours([]);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (day: SpecialDay) => {
    setEditing(day);
    setDate(day.date);
    setIsClosed(day.isClosed);
    setHours(day.hours.map((h) => ({ open: h.open, close: h.close })));
    setFormError(null);
    setDialogOpen(true);
  };

  const submit = () => {
    if (!date) {
      setFormError('日付を入力してください。');
      return;
    }
    if (!isClosed) {
      if (hours.length === 0) {
        setFormError('特別営業の場合は営業時間帯を1件以上入力してください。');
        return;
      }
      if (hours.some((h) => !isValidTimeRange(h))) {
        setFormError('時間帯に未入力のものがあります。');
        return;
      }
    }
    setBusy(true);
    dispatch(upsertSpecialDay({ storeId, date, isClosed, hours: isClosed ? [] : hours }))
      .unwrap()
      .then(() => {
        setDialogOpen(false);
        showSaved();
        return load(); // upsert は自動再取得しないため自前で再フェッチ
      })
      .catch((rejected: ReservationConflict) => {
        // B4: 臨時休業化でその日に予約があると BE が 409。専用メッセージをダイアログ内に表示する。
        if (isClosed && rejected?.status === 409) {
          const countNote =
            typeof rejected.affectedCount === 'number'
              ? `（この日の予約: ${rejected.affectedCount}件）`
              : '';
          setFormError(
            `この日には予約が入っているため臨時休業にできません。先に予約をすべてキャンセルしてください。${countNote}`,
          );
          return;
        }
        showError(rejected);
      })
      .finally(() => setBusy(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setBusy(true);
    dispatch(deleteSpecialDay({ storeId, specialDayId: deleteTarget.id }))
      .unwrap()
      .then(() => {
        showSaved();
        return load(); // delete も自動再取得しないため自前で再フェッチ
      })
      .catch(showError)
      .finally(() => {
        setDeleteTarget(null);
        setBusy(false);
      });
  };

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sorted = [...specialDays].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Box>
      <TabDescription text="臨時休業や特別営業など、特定の日だけ通常と異なる設定にできます" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
          特定日を追加
        </Button>
      </Box>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          特定日（臨時休業・特別営業）はまだ登録されていません。
        </Typography>
      ) : (
        <Paper elevation={2}>
          <List dense>
            {sorted.map((day) => (
              <ListItem
                key={day.id}
                secondaryAction={
                  <>
                    <IconButton size="small" onClick={() => openEdit(day)} aria-label="編集">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(day)}
                      aria-label="削除"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{formatDate(day.date)}</Typography>
                      <Chip
                        size="small"
                        label={day.isClosed ? '臨時休業' : '特別営業'}
                        color={day.isClosed ? 'default' : 'success'}
                      />
                    </Box>
                  }
                  secondary={
                    day.isClosed
                      ? null
                      : day.hours.map((h) => formatTimeRange(h)).join(' / ')
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* 追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? '特定日を編集' : '特定日を追加'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {formError}
            </Alert>
          )}
          <TextField
            label="日付"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            margin="dense"
            size="small"
            InputLabelProps={{ shrink: true }}
            disabled={editing !== null} // 日付単位の upsert のため編集時は日付固定
          />
          <FormControlLabel
            control={<Switch checked={isClosed} onChange={(e) => setIsClosed(e.target.checked)} />}
            label="終日休業"
          />
          {!isClosed && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                この日の営業時間帯（通常の営業時間を上書きします）
              </Typography>
              <TimeRangeListEditor ranges={hours} onChange={setHours} disabled={busy} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDialogOpen(false)} disabled={busy}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={submit} disabled={busy}>
            {busy ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="特定日の削除"
        message={`${deleteTarget ? formatDate(deleteTarget.date) : ''} の設定を削除します。よろしいですか？`}
        confirmText="削除する"
        confirmColor="error"
        closeColor="inherit"
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default SpecialDaysTab;
